package com.fantasytenis.shared.data.auth.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.WrappedResponse
import com.fantasytenis.shared.data.auth.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*

/**
 * Auth API client.
 *
 * Handles all authentication-related HTTP requests.
 */
class AuthApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    /**
     * Register a new user.
     */
    suspend fun register(request: RegisterRequest): ApiResponse<AuthResponse> {
        return try {
            val response = httpClient.post("${baseUrl}public/auth/register") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<AuthResponse>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Registration failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<AuthResponse>>()
                ApiResponse.Error(wrapped.error?.message ?: "Registration failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Login user.
     */
    suspend fun login(request: LoginRequest): ApiResponse<AuthResponse> {
        return try {
            val response = httpClient.put("${baseUrl}public/auth/login") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<AuthResponse>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Login failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<AuthResponse>>()
                ApiResponse.Error(wrapped.error?.message ?: "Invalid credentials", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Refresh access token.
     */
    suspend fun refreshToken(request: RefreshTokenRequest): ApiResponse<TokenResponse> {
        return try {
            val response = httpClient.post("${baseUrl}public/auth/refresh") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<TokenResponse>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Token refresh failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<TokenResponse>>()
                ApiResponse.Error(wrapped.error?.message ?: "Token refresh failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Logout user.
     */
    suspend fun logout(): ApiResponse<Unit> {
        return try {
            val response = httpClient.post("${baseUrl}auth/logout")

            if (response.status.isSuccess()) {
                ApiResponse.Success(Unit)
            } else {
                val wrapped = response.body<WrappedResponse<Unit>>()
                ApiResponse.Error(wrapped.error?.message ?: "Logout failed", response.status.value)
            }
        } catch (e: Exception) {
            // Even if logout fails on server, we want to clear local state
            ApiResponse.Success(Unit)
        }
    }

    /**
     * Get current user profile.
     */
    suspend fun getCurrentUser(): ApiResponse<UserDto> {
        return try {
            val response = httpClient.get("${baseUrl}auth/me")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<UserDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get user", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<UserDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get user", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Update user profile.
     */
    suspend fun updateUser(request: UpdateUserRequest): ApiResponse<UserDto> {
        return try {
            val response = httpClient.patch("${baseUrl}auth/me") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<UserDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Update failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<UserDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Update failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Change password.
     */
    suspend fun changePassword(request: ChangePasswordRequest): ApiResponse<Unit> {
        return try {
            val response = httpClient.post("${baseUrl}auth/change-password") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                ApiResponse.Success(Unit)
            } else {
                val wrapped = response.body<WrappedResponse<Unit>>()
                ApiResponse.Error(wrapped.error?.message ?: "Password change failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Check if username is available.
     */
    suspend fun checkUsernameAvailable(username: String): ApiResponse<CheckUsernameData> {
        return try {
            val response = httpClient.get("${baseUrl}public/auth/username/check") {
                parameter("username", username)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<CheckUsernameData>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Check failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<CheckUsernameData>>()
                ApiResponse.Error(wrapped.error?.message ?: "Check failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get signed URL for avatar upload.
     */
    suspend fun getAvatarUploadUrl(contentType: String): ApiResponse<AvatarUploadData> {
        return try {
            val request = GetAvatarUploadUrlRequest(contentType = contentType)
            val response = httpClient.post("${baseUrl}auth/avatar/upload-url") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<AvatarUploadData>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get upload URL", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<AvatarUploadData>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get upload URL", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get list of preset avatars.
     */
    suspend fun getPresetAvatars(): ApiResponse<PresetAvatarsData> {
        return try {
            val response = httpClient.get("${baseUrl}public/auth/avatars/presets")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<PresetAvatarsData>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get presets", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<PresetAvatarsData>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get presets", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
